import * as React from 'react';
import { Id, Bem, createChildIsTypeOf, getKeyboardFocusableElements, warnOnce, omitKeys } from '../../utils';
import { HeaderMenuGroup } from './HeaderMenuGroup';
import { HeaderMenuItem } from './HeaderMenuItem';
import './HeaderMenu.css';
import { HeaderItem } from './HeaderItem';
import { Key } from '../../constants';
const bem = Bem.create('vai-header-menu');
export class HeaderMenu extends React.Component {
    constructor() {
        super(...arguments);
        this.state = { open: !!this.props.openOnMount, children: [] };
        this.dropDownRef = React.createRef();
        this.menuButtonRef = React.createRef();
        this.handleOutsideClicks = (event) => {
            if (!this.elementContainsEventTarget(this.menuButtonRef.current, event.target) &&
                !this.elementContainsEventTarget(this.dropDownRef.current, event.target)) {
                this.setState({ open: false });
                this.removeOutsideClickEventHandlers();
            }
        };
        this.removeOutsideClickEventHandlers = () => {
            document.removeEventListener('mousedown', this.handleOutsideClicks, { capture: true });
        };
        this.handleDropDownKeyDown = (event) => {
            const { target } = event;
            if (this.dropDownRef.current === null ||
                ![Key.Home, Key.Escape, Key.End, Key.ArrowDown, Key.ArrowUp, Key.Tab].includes(event.key)) {
                return;
            }
            const childrenArray = getKeyboardFocusableElements(this.dropDownRef.current);
            if (childrenArray === null || childrenArray.length === 0) {
                return;
            }
            childrenArray[childrenArray.length - 1].classList.add('vai-header-menu-last-item');
            childrenArray[0].classList.add('vai-header-menu-first-item');
            let newFocusTarget = null;
            if (event.shiftKey && event.key === Key.Tab) {
                if (target && target.classList.contains('vai-header-menu-first-item')) {
                    this.handleClose();
                    event.preventDefault();
                }
                return;
            }
            switch (event.key) {
                case Key.ArrowDown:
                    newFocusTarget = target.nextElementSibling || this.dropDownRef.current.firstChild;
                    break;
                case Key.ArrowUp:
                    newFocusTarget = target.previousElementSibling || this.dropDownRef.current.lastChild;
                    break;
                case Key.Tab:
                    if (target && target.classList.contains('vai-header-menu-last-item')) {
                        this.handleClose();
                        event.preventDefault();
                    }
                    break;
                case Key.Home:
                    newFocusTarget = this.dropDownRef.current.firstChild;
                    break;
                case Key.End:
                    newFocusTarget = this.dropDownRef.current.lastChild;
                    break;
                case Key.Escape:
                    this.handleClose();
                    break;
            }
            if (newFocusTarget !== null) {
                newFocusTarget.focus();
                event.preventDefault();
            }
        };
        this.elementContainsEventTarget = (element, target) => {
            return !!(element && element.contains(target));
        };
        this.handleClose = () => {
            if (this.menuButtonRef.current) {
                this.menuButtonRef.current.focus();
            }
            this.setState({ open: false });
            this.removeOutsideClickEventHandlers();
        };
        this.handleMenuButtonKeyDown = (event) => {
            if ([' ', Key.Enter, Key.ArrowDown].includes(event.key)) {
                const { open } = this.state;
                if (Key.ArrowDown === event.key && open === true) {
                    return;
                }
                this.setState({ open: !open });
                if (this.dropDownRef.current) {
                    const firstFocusableItem = getKeyboardFocusableElements(this.dropDownRef.current);
                    if (firstFocusableItem !== null && firstFocusableItem[0] !== undefined) {
                        setTimeout(() => {
                            firstFocusableItem[0].focus();
                        }, 200);
                    }
                    else {
                        warnOnce('Could not move keyboard focus to the first focusable element because there was none.' +
                            'Use HeaderMenuItem component with "a", "button" or eg. react-router Link.');
                    }
                }
            }
        };
        this.handleToggle = () => {
            const { open } = this.state;
            this.setState({ open: !open });
            if (open) {
                this.removeOutsideClickEventHandlers();
            }
        };
    }
    static getDerivedStateFromProps(props) {
        const childrenArray = React.Children.toArray(props.children);
        const dropdownItems = childrenArray.reduce(HeaderMenu.collectDropdownItems, []);
        const childHasIcon = dropdownItems.some((child) => !!child.props.iconName);
        return { children: childHasIcon ? HeaderMenu.addEmptyIconPropToDropdownItemWithoutIcon(childrenArray) : props.children };
    }
    static collectDropdownItems(acc, child) {
        if (HeaderMenu.childIsDropdownItem(child)) {
            return [...acc, child];
        }
        else if (HeaderMenu.childIsDropDownGroup(child)) {
            return React.Children.toArray(child.props.children).reduce(HeaderMenu.collectDropdownItems, acc);
        }
        return acc;
    }
    static addEmptyIconPropToDropdownItemWithoutIcon(children) {
        return children.map((child) => {
            if (HeaderMenu.childIsDropdownItem(child)) {
                if (!child.props.iconName) {
                    return React.cloneElement(child, { iconName: 'empty' });
                }
            }
            else if (HeaderMenu.childIsDropDownGroup(child)) {
                return React.cloneElement(child, undefined, HeaderMenu.addEmptyIconPropToDropdownItemWithoutIcon(React.Children.toArray(child.props.children)));
            }
            return child;
        });
    }
    render() {
        const { children, open } = this.state;
        const omitProps = ['openOnMount'];
        const filteredProps = omitKeys(this.props, omitProps);
        const { title, iconName, iconColor, htmlId, ...rest } = filteredProps;
        const id = Id.create(htmlId, ['header dropdown']);
        return (React.createElement("div", { id: id.toString(), "data-ta": this.props.dataTa, className: bem.block() },
            React.createElement(HeaderItem, Object.assign({ innerRef: this.menuButtonRef, component: "button", className: bem.element('button')(), iconColor: iconColor, iconName: iconName, title: title, isOpen: open, onMouseDown: this.handleToggle, onKeyDown: this.handleMenuButtonKeyDown, "aria-haspopup": "true", "aria-expanded": open ? 'true' : 'false' }, rest)),
            children && (React.createElement("div", { id: id.copyWith(['content']).toString(), className: bem.element('children')(open ? 'open' : 'closed'), onClick: this.handleClose, onKeyDown: this.handleDropDownKeyDown, role: "menu", ref: this.dropDownRef }, children))));
    }
    componentDidMount() {
        if (this.props.openOnMount) {
            document.addEventListener('mousedown', this.handleOutsideClicks, { capture: true });
        }
    }
    componentDidUpdate() {
        if (!this.state.open) {
            return;
        }
        document.addEventListener('mousedown', this.handleOutsideClicks, { capture: true });
    }
    componentWillUnmount() {
        this.removeOutsideClickEventHandlers();
    }
}
HeaderMenu.defaultProps = {
    openOnMount: false,
};
HeaderMenu.childIsDropDownGroup = createChildIsTypeOf(HeaderMenuGroup);
HeaderMenu.childIsDropdownItem = createChildIsTypeOf(HeaderMenuItem);
//# sourceMappingURL=HeaderMenu.js.map